import styled, { css } from 'styled-components';
import { themeToken } from '../../utils/cpds-utils';
import { mediaQueries } from '../../token/shared/common';

const buttonSizes = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
};

const getButtonSizeStyles = (size) => {
  switch (size) {
    case 'small':
      return CpdsButtonSmallStyle;
    case 'medium':
      return CpdsButtonMediumStyle;
    case 'large':
      return CpdsButtonLargeStyle;
    default:
      return CpdsButtonLargeStyle;
  }
};

const getTokenValues = (token, props, size) => css`
  --button-${size}-height: ${token[`button_${size}_max_height`] || 'auto'};
  --button-${size}-width: ${props.isIconButton ? token[`button_${size}_max_width`] : props.isFullWidth ? '100%' : 'auto'};
  --button-${size}-min-width: ${!props.isIconButton ? token[`button_${size}_min_width`] : token[`button_${size}_max_width`]};
  --button-${size}-font-size: ${token[`button_${size}_font_size`] || '12px'};
  --button-${size}-padding: ${token[`button_${size}_padding`] || '0px 12px'};
  --button-${size}-line-height: ${token[`button_${size}_font_size`] || '12px'};
`;

const ButtonSizeVariables = (token, props) => css`
  ${getTokenValues(token, props, 'small')}
  ${getTokenValues(token, props, 'medium')}
  ${getTokenValues(token, props, 'large')}
  --button-mobile-width: ${props.isIconButton ? '32px' : 'auto'};
`;

const CpdsButtonStyle = css`
  border: 1px solid transparent;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (${mediaQueries.mobile}) {
    max-height: 32px;
    max-width: var(--button-mobile-width);
  }

  &:disabled {
    cursor: not-allowed;
  }

  ${(props) => props.styleOverrides}
`;

const CpdsLinkStyle = css`
  ${CpdsButtonStyle}
`;

const CpdsButtonSmallStyle = css`
  height: var(--button-small-height);
  width: var(--button-small-width);
  min-width: var(--button-small-min-width);
  font-size: var(--button-small-font-size);
  padding: var(--button-small-padding);
  line-height: var(--button-small-line-height);
`;

const CpdsButtonMediumStyle = css`
  height: var(--button-medium-height);
  width: var(--button-medium-width);
  min-width: var(--button-medium-min-width);
  font-size: var(--button-medium-font-size);
  padding: var(--button-medium-padding);
  line-height: var(--button-medium-line-height);
`;

const CpdsButtonLargeStyle = css`
  height: var(--button-large-height);
  width: var(--button-large-width);
  min-width: var(--button-large-min-width);
  font-size: var(--button-large-font-size);
  padding: var(--button-large-padding);
  line-height: var(--button-large-line-height);
`;

const getButtonStyle = (cpds, token, props) => {
  let backgroundColor, textColor, hoverBackgroundColor, textHoverColor, backgroundDisabledColor, textDisabledColor, fontWeight, activeBorderColor, activeBackgroundColor, activeTextColor;
  switch (props.buttonType) {
    case 'Primary':
      backgroundColor = token?.button_primary_background_color || '#1e2431';
      textColor = token?.button_primary_text_color || '#ffffff';
      fontWeight = token?.button_primary_font_weight || '500';
      textHoverColor = token?.button_primary_text_hover_color || '#ffffff';
      hoverBackgroundColor = token?.button_primary_background_hover_color || '#0C0E1C';
      backgroundDisabledColor = token?.button_primary_background_disabled_color || '#F5F5F5';
      textDisabledColor = token?.button_primary_text_disabled_color || '#F5F5F5';
      activeBackgroundColor = token?.button_primary_focus_background_color || '#0E3672';
      activeTextColor = token?.button_primary_focus_text_color || '#FFFFFF';
      activeBorderColor = token?.button_primary_focus_border_color || '#1e2431';
      break;
    case 'Secondary':
      backgroundColor = token?.button_secondary_background_color || '#ffffff';
      textColor = token?.button_secondary_text_color || '#0C0E1C';
      fontWeight = token?.button_secondary_font_weight || '500';
      textHoverColor = token?.button_secondary_text_hover_color || '#0C0E1C';
      hoverBackgroundColor = token?.button_secondary_background_hover_color || '#F5F5F5';
      backgroundDisabledColor = token?.button_secondary_background_disabled_color || '#FFFFFF';
      textDisabledColor = token?.button_secondary_text_disabled_color || '#ffffff';
      activeBackgroundColor = token?.button_secondary_focus_background_color || '#0E3672';
      activeTextColor = token?.button_secondary_focus_text_color || '#FFFFFF';
      activeBorderColor = token?.button_secondary_focus_border_color || '#1e2431';
      break;
    case 'Tertiary':
      backgroundColor = props?.isIconButton ? token?.button_tertiary_background_color || '#ffffff' : 'unset';
      textColor = token?.button_tertiary_text_color || '#0C0E1C';
      fontWeight = token?.button_tertiary_font_weight || '500';
      textHoverColor = token?.button_tertiary_text_hover_color || '#0C0E1C';
      hoverBackgroundColor = props?.isIconButton ? token?.button_tertiary_background_hover_color || '#F5F5F5' : 'unset';
      backgroundDisabledColor = props?.isIconButton ? token?.button_tertiary_background_disabled_color || '#FFFFFF' : 'unset';
      textDisabledColor = token?.button_tertiary_text_disabled_color || '#ffffff';
      activeBackgroundColor = token?.button_tertiary_background_active_color || '#0E3672';
      activeTextColor = token?.button_tertiary_text_active_color || '#FFFFFF';
      activeBorderColor = token?.button_tertiary_focus_border_color || '#1e2431';
      break;
    case 'Floating':
      backgroundColor = token?.button_floating_background_color || '#ffffff';
      textColor = token?.button_floating_text_color || '#0C0E1C';
      hoverBackgroundColor = token?.button_floating_background_hover_color || '#F5F5F5';
      textHoverColor = token?.button_floating_text_hover_color || '#0C0E1C';
      backgroundDisabledColor = token?.button_floating_background_disabled_color || '#FFFFFF';
      textDisabledColor = token?.button_floating_text_disabled_color || '#9CA0B0';
      fontWeight = token?.button_floating_font_weight || '500';
      activeBackgroundColor = token?.button_floating_background_active_color || '#F2F2F2';
      activeTextColor = token?.button_floating_text_active_color || '#858585';
      break;
    default:
      backgroundColor = token?.button_primary_background_color || '#1e2431';
      textColor = token?.button_primary_text_color || '#ffffff';
      hoverBackgroundColor = token?.button_primary_background_hover_color || '#0C0E1C';
      textHoverColor = token?.button_primary_text_hover_color || '#ffffff';
      backgroundDisabledColor = token?.button_primary_background_disabled_color || '#F5F5F5';
      textDisabledColor = token?.button_primary_text_disabled_color || '#F5F5F5';
      break;
  }

  const themeType = cpds?.themeType || 'brand';
  return css`
    background-color: ${backgroundColor};
    color: ${textColor};
    font-weight: ${fontWeight};
    position: relative;
    border-radius: ${props?.isIconButton ? '50%' : '34px'};
    padding: ${props?.isIconButton && 'unset'};
    
    &:hover {
      color: ${textHoverColor};
      background-color: ${hoverBackgroundColor};
    }

    --button-text-color: ${textColor};
 
    ${themeType === 'brand' &&
    css`
      &:disabled {
        background-color: ${backgroundDisabledColor};
        color: ${textDisabledColor};
        --button-text-color: ${textDisabledColor};
        opacity: 1;
      }
      &:focus {
        outline: none;
        border: none;
      }
      &::after {
        content: "";
        position: absolute;
        border: 1px dashed ${activeBorderColor};
        border-radius: inherit;
        pointer-events: none;
        opacity: 0;
      }
      &:focus-visible::after {
        opacity: 1;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
      }
    `}
    ${themeType !== 'brand' &&
    css`
      &:disabled {
        background-color: ${backgroundDisabledColor};
        color: ${textDisabledColor};
        --button-text-color: ${textDisabledColor};
      }
      &:active {
        background-color: ${activeBackgroundColor};
        color: ${activeTextColor};
      }
    `}

    ${(props) => props.styleOverrides}
  `;
};

const CpdsButtonPrimaryStyle = styled.button`
  ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      ${ButtonSizeVariables(token, props)}
      ${CpdsButtonStyle}
      ${getButtonSizeStyles(props.buttonSize)}
      ${getButtonStyle(cpds, token, props)}
    `;
  }}
`;

const CpdsButtonSecondaryStyle = styled(CpdsButtonPrimaryStyle)`
  ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      border: 1px solid ${token?.button_secondary_border_color || '#D1D5E2'};
      &:focus {
        outline: none;
        border: 1px solid ${token?.button_secondary_border_color || '#D1D5E2'};
      }
      &:disabled {
        border: 1px solid ${token?.button_secondary_disabled_border_color || '#E2E4EC'};
      }
    `;
  }}
`;

const CpdsButtonTertiaryStyle = styled(CpdsButtonPrimaryStyle)`
  ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    const themeType = cpds?.themeType || 'brand';
    const size = props.buttonSize;
    return css`
      border: none;
      outline: none;
      &:disabled {
        border: none;
        color: ${token?.button_tertiary_text_disabled_color || '#9CA0B0'};
      }
      &:focus-visible::after {
        top: -4px;
        left: 0px;
        right: 0px;
        bottom: -4px;
      }
      span.cta-copy {
        padding-bottom: 4px;
        padding-top: 4px;
      }

      ${props?.isIconButton && themeType == 'brand' && css`
         height: ${token?.button_tertiary_height || '24px'};
         width: ${token?.button_tertiary_height || '24px'};
         min-width: unset;
         &:focus-visible::after {
          top: 0px;
          left: 0px;
          right: 0px;
          bottom: 0px;
        }
      `}
      ${themeType == 'brand' && css`
          height: ${token?.button_tertiary_height || '24px'};
          &:hover {
          span.cta-copy {
            border-bottom: 1px solid ${token?.button_tertiary_focus_text_underline_color || '#D1D5E2'};
          }
      }

        `}
        ${themeType !== 'brand' && css`
          &:hover { 
                color: ${token?.button_tertiary_text_hover_color || '#676A7D'};
          }
          &:active {
            color: ${token?.button_tertiary_text_active_color}
          }
        `}
    `;
  }}
`;

const CpdsButtonFloatingStyle = styled(CpdsButtonPrimaryStyle)`

 ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      height: ${token?.button_floating_height || '36px'};
      box-shadow: 0px 2px 12px 0px ${token?.button_floating_border_color};
      &:active {
        background-color: ${token?.button_floating_background_active_color || '#E0E0E0'};
        color: ${token?.button_floating_text_active_color || '#858585'};
      }
      &::after {
        display: none;
      }
      &:disabled {
        opacity: 0.5;
      }
    `;
  }}
`;

const CpdsButtonLinkStyle = styled.a`
  ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);

    return css`
      ${CpdsButtonStyle}
      cursor: pointer;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      text-decoration: none;
      color: ${props?.disabled ? token?.button_link_text_disabled_color || '#9CA0B0' : token?.button_link_text_color || '#ffffff'};
      --button-text-color: ${props?.disabled ? token?.button_link_text_disabled_color || '#9CA0B0' : token?.button_link_text_color || '#ffffff'};

      &:focus-visible {
        outline: none;
        border: 2px dashed rgba(30, 36, 49, 0.6);
      }
      font-size: ${token?.button_link_font_size || '12px'};
      font-weight: ${token?.button_link_font_weight || '500'};
      
      pointer-events: ${props?.disabled ? 'none' : 'auto'};
      cursor: ${props?.disabled ? 'not-allowed' : 'pointer'};
      ${(props) => props.styleOverrides}


    `;
  }}
`;

const CpdsButtonLinkPrimaryStyle = styled(CpdsButtonLinkStyle)`
  ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      ${ButtonSizeVariables(token, props)}
      ${CpdsLinkStyle}
      ${getButtonSizeStyles(props.buttonSize)}
      ${getButtonStyle(cpds, token, props)}

      &:hover {
        text-decoration: none;
      }
    `;
  }}
`;

const CpdsButtonLinkSecondaryStyle = styled(CpdsButtonLinkStyle)`
  ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      ${ButtonSizeVariables(token, props)}
      ${CpdsButtonLinkStyle}
      ${getButtonSizeStyles(props.buttonSize)}
      ${getButtonStyle(cpds, token, props)}

      &:focus {
        outline: none;
        border: 1px solid ${token?.button_secondary_border_color || '#D1D5E2'};
      }
      &:disabled {
        border: 1px solid ${token?.button_secondary_disabled_border_color || '#E2E4EC'};
      }
      &:hover {
        text-decoration: none;
      }
    `;
  }}
`;

const CpdsButtonLinkTertiaryStyle = styled(CpdsButtonLinkStyle)`
  ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      ${ButtonSizeVariables(token, props)}
      ${CpdsButtonLinkStyle}
      ${props?.isIconButton && getButtonSizeStyles(props.buttonSize)}
      ${getButtonStyle(cpds, token, props)}
      &:focus {
        border: none;
        background-color: ${props?.isIconButton ? token?.button_tertiary_background_active_color || '#F5F5F5' : 'unset'};
        color: ${token?.button_tertiary_text_active_color || '#0C0E1C'};
      }
    `;
  }}
`;

const CpdsButtonLinkFloatingStyle = styled(CpdsButtonLinkStyle)`
  ${({ theme: { styles: { cpds } }, ...props }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, props?.themeColorMode);
    return css`
      ${ButtonSizeVariables(token, props)}
      ${CpdsButtonLinkStyle}
      ${getButtonSizeStyles(props.buttonSize)}
      ${getButtonStyle(cpds, token, props)}
    `;
  }}
`;

const IconWrapper = styled.span`
  height: auto;
  display: flex;
  svg {
    path {
      fill: var(--button-text-color);
    }
  }
`;

const TextWrapper = styled.span``;

export {
  CpdsButtonStyle,
  CpdsButtonLinkStyle,
  CpdsButtonLinkPrimaryStyle,
  CpdsButtonLinkSecondaryStyle,
  CpdsButtonLinkTertiaryStyle,
  CpdsButtonLinkFloatingStyle,
  CpdsButtonPrimaryStyle,
  CpdsButtonSecondaryStyle,
  CpdsButtonTertiaryStyle,
  CpdsButtonFloatingStyle,
  IconWrapper,
  TextWrapper,
};
